import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { WarningTriangle } from 'iconoir-react';

type ConsentStatus = 'none' | 'granted' | 'revoke_requested' | 'revoked' | 'reauth_requested' | undefined;

interface ConsentGateModalProps {
  onNavigateToConsent: () => void;
  onNavigateToProfile: () => void;
  /** Current hipaaConsentState.status — undefined for pre-migration patients */
  consentStatus?: ConsentStatus;
}

function getGateContent(status: ConsentStatus): { title: string; message: string; action: 'consent' | 'blocked' } {
  switch (status) {
    case 'revoked':
      return {
        title: 'HIPAA Authorization Revoked',
        message:
          'Your HIPAA authorization has been revoked. Your emergency access card is no longer active. You may request to re-open your application from the Profile page.',
        action: 'blocked',
      };
    case 'revoke_requested':
      return {
        title: 'Revocation In Progress',
        message:
          'Your HIPAA revocation request is being reviewed. Your emergency access card remains active until the revocation is approved.',
        action: 'blocked',
      };
    case 'reauth_requested':
      return {
        title: 'Re-Authorization Pending',
        message:
          'Your request to re-open your application is being reviewed. You will be able to complete new consent forms once approved.',
        action: 'blocked',
      };
    default:
      return {
        title: 'Consent Required',
        message:
          'Please complete your consent forms before continuing. You need to review and sign the HIPAA Authorization, Consent for Care, and End User License Agreement.',
        action: 'consent',
      };
  }
}

/**
 * Modal that blocks non-exempt routes when HIPAA consent is incomplete.
 * Displays state-specific messaging based on hipaaConsentState.
 *
 * @see CU-86agmta3g
 */
export const ConsentGateModal = ({ onNavigateToConsent, onNavigateToProfile, consentStatus }: ConsentGateModalProps) => {
  const { title, message, action } = getGateContent(consentStatus);

  return (
    <Dialog open={true} onClose={() => {}} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="align-center flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:data-closed:translate-y-0 sm:data-closed:scale-95"
          >
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
                <WarningTriangle aria-hidden="true" className="h-6 w-6 text-amber-600" />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              {action === 'consent' ? (
                <button
                  type="button"
                  onClick={onNavigateToConsent}
                  className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-500 sm:w-auto"
                >
                  Go to Consent Forms
                </button>
              ) : (
                <div className="flex w-full gap-3 sm:w-auto">
                  {consentStatus === 'revoked' && (
                    <button
                      type="button"
                      onClick={onNavigateToProfile}
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-500 sm:w-auto"
                    >
                      Go to Profile
                    </button>
                  )}
                  <a
                    href="mailto:info@mideohealth.com"
                    className="inline-flex w-full justify-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-xs hover:bg-gray-200 sm:w-auto"
                  >
                    Contact Support
                  </a>
                </div>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
